import { Box, Stack } from '@mui/material'
import { useEffect } from 'react'
import { useMeasure } from 'react-use'

import { useStatsigGate } from '~/configs/statsig'
import DesktopTitleBar from '~/features/desktop/DesktopTitleBar'
import { clearCloakedOrgPath } from '~/layouts/components/orgHierarchy/cloakSessionStorage'
import OrgDocumentTitleSuffix from '~/layouts/components/orgHierarchy/OrgDocumentTitleSuffix'
import OrgHierarchyBar from '~/layouts/components/orgHierarchy/OrgHierarchyBar'
import { useBannerOffsetControls } from '~/store/slices/display'

import CloakingBanner from './CloakingBanner'
import DemoModeBanner from './DemoModeBanner'
import TimezoneBanner from './TimezoneBanner'

export default function Banners() {
  const setBannerOffset = useBannerOffsetControls()
  const { enabled: orgHierarchyBarEnabled, isLoading: orgHierarchyBarLoading } =
    useStatsigGate('org_hierarchy_bar')

  // Gate-off clear must live here — `useOrgContext` only mounts under
  // OrgHierarchyBar, which unmounts as soon as the gate flips off, so a clear
  // inside that hook never runs. Wait until Statsig finishes loading: the gate
  // reads false during init, and clearing then would wipe a tab refresh restore
  // before OrgHierarchyBar can remount.
  useEffect(() => {
    if (orgHierarchyBarLoading || orgHierarchyBarEnabled) return
    clearCloakedOrgPath()
  }, [orgHierarchyBarLoading, orgHierarchyBarEnabled])

  // Make the height of the banner bar available to the parent component. This is needed to offset
  // the nav drawer, app bar header and main content.
  const [ref, { height }] = useMeasure()
  useEffect(() => {
    setBannerOffset(height)
  }, [height, setBannerOffset])

  return (
    <Box
      ref={ref}
      sx={(theme) => ({
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bgcolor: 'background.paper',
        zIndex: theme.zIndex.drawer + 1
      })}
    >
      <Stack>
        {/* Window chrome when running inside the desktop shell (nothing in a
            browser). Lives in this fixed, measured box so its height feeds the
            same bannerOffset that shifts the nav drawer and content down. */}
        <DesktopTitleBar />
        <DemoModeBanner />
        {orgHierarchyBarEnabled ? <OrgHierarchyBar /> : <CloakingBanner />}
        {orgHierarchyBarEnabled ? <OrgDocumentTitleSuffix /> : null}
        <TimezoneBanner />
      </Stack>
    </Box>
  )
}
